import { DemandPosition } from '../../services/demandService';
import { StackedBarChartElement } from '../../sp-types/dashboardTypes';
import { Material } from '../../sp-types/materialType';
import { MonthYear } from '../../utils/MonthYear/monthYear';
import { GENERAL_ID } from '../../components/Stores/exposureDataStore';
import { getExposureWindow, filterToHorizon } from '../../utils/exposureHorizon';
import { toNumber } from '../../utils/positionUtils';

/**
 * Simplified demand adapter that directly transforms demand positions to chart format
 * Matches the pattern used by hedgesAdapter and supplyAdapter
 */

type DemandByFormula = Record<string, { quantities: Record<string, number>; monetary: Record<string, number> }>;

const getMonthKey = (date: MonthYear): string => `${date.year}-${date.month}`;

/**
 * Aggregate demand positions by month - returns both quantity and monetary values
 */
const aggregateDemandByMonth = (
  demands: DemandPosition[],
  pricesMap: Record<string, number>,
  getRateToBase: (currency: string) => number | null,
  baseCurrency: string,
): DemandByFormula => {
  const result: DemandByFormula = {};

  demands.forEach((demand) => {
    const formulaCode = demand.formula_code;
    const deliveryDate = new Date(demand.delivery_period);
    const year = deliveryDate.getFullYear();
    const month = deliveryDate.getMonth() + 1; // 1-indexed
    const monthYear: MonthYear = { year, month };
    const key = getMonthKey(monthYear);

    if (!result[formulaCode]) {
      result[formulaCode] = { quantities: {}, monetary: {} };
    }

    const quantity = toNumber(demand.quantity);

    // Use spot price from pricesMap if available, otherwise use contract price with currency conversion
    let unitPrice = pricesMap[formulaCode];
    if (!unitPrice && demand.unit_price_amount) {
      let contractPrice = toNumber(demand.unit_price_amount);
      const unitPriceCurrency = demand.unit_price_currency || baseCurrency;
      
      // Convert contract price to base currency
      if (unitPriceCurrency.toUpperCase() !== baseCurrency.toUpperCase()) {
        const rate = getRateToBase(unitPriceCurrency);
        if (rate !== null && rate !== 0) {
          contractPrice = contractPrice / rate;
        }
      }
      unitPrice = contractPrice;
    }
    unitPrice = unitPrice || 0;

    const monetaryValue = quantity * unitPrice;

    result[formulaCode].quantities[key] = (result[formulaCode].quantities[key] || 0) + quantity;
    result[formulaCode].monetary[key] = (result[formulaCode].monetary[key] || 0) + monetaryValue;
  });

  return result;
};

/**
 * Fill in months based on exposure horizon (configurable, default 12 months)
 */
const fillMonthsArray = (
  monthlyValues: Record<string, number>,
  horizonMonths: number = 12
): { date: MonthYear; value: number }[] => {
  const now = new Date();
  const result: { date: MonthYear; value: number }[] = [];

  for (let i = 0; i < horizonMonths; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const monthYear: MonthYear = {
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    };
    const key = getMonthKey(monthYear);
    result.push({
      date: monthYear,
      value: monthlyValues[key] || 0,
    });
  }

  return result;
};

/**
 * Main adapter function: Transform demand positions to stacked bar chart elements
 */
export const transformDemandToStackedBar = (
  demands: DemandPosition[] | undefined,
  selectedFormulaId: string,
  materialsMap: Record<string, Material>,
  colorsMap: Record<string, string>,
  pricesMap: Record<string, number>,
  isQuantity: boolean,
  horizonMonths: number = 12,
  getRateToBase?: (currency: string) => number | null,
  baseCurrency: string = 'USD',
): StackedBarChartElement[] => {
  if (!demands || demands.length === 0) {
    return [];
  }

  // Default getRateToBase if not provided (no conversion)
  const defaultGetRateToBase = (currency: string) => currency.toUpperCase() === baseCurrency.toUpperCase() ? 1 : null;
  const rateConverter = getRateToBase || defaultGetRateToBase;

  const aggregated = aggregateDemandByMonth(demands, pricesMap, rateConverter, baseCurrency);
  const isGeneralMode = selectedFormulaId === GENERAL_ID;
  const formulasToShow = isGeneralMode
    ? Object.keys(aggregated)
    : Object.keys(aggregated).filter(f => f === selectedFormulaId);

  const result: StackedBarChartElement[] = formulasToShow.map((formulaCode) => {
    const { quantities, monetary } = aggregated[formulaCode] || { quantities: {}, monetary: {} };
    
    const quantityArray = fillMonthsArray(quantities, horizonMonths);
    const monetaryArray = fillMonthsArray(monetary, horizonMonths);
    
    // Calculate sums
    const sumQuantity = quantityArray.reduce((sum, item) => sum + toNumber(item.value), 0);
    const sumMonetary = monetaryArray.reduce((sum, item) => sum + toNumber(item.value), 0);

    return {
      formulaId: formulaCode,
      fieldName: materialsMap[formulaCode]?.name || formulaCode,
      unit: materialsMap[formulaCode]?.unit,
      price: pricesMap[formulaCode] || 0,
      color: colorsMap[formulaCode] || '#6b7280',
      valueArray: isQuantity ? quantityArray : monetaryArray, // For backward compatibility
      quantityArray,
      monetaryArray,
      marketMonetaryArray: monetaryArray, // For demand, market = actual (always spot prices)
      sumValue: isQuantity ? sumQuantity : sumMonetary, // For backward compatibility
      sumQuantity,
      sumMonetary,
      sumMarketMonetary: sumMonetary, // For demand, market = actual (always spot prices)
      totalWeight: sumQuantity,
    };
  });

  return result;
};
